import { useEffect, useRef, useState } from "react";
import useJouneyReq from "../../../../components/hook/bot-req";
import { ActionCategory, ActionDef, JourneyCategory, JourneyTemplate, TriggerDef } from "./types";
import { setActionCatalog, setJourneyCategories } from "./catalog";

// Backend catalog for the Customer Journey builder:
//   GET /v1/bot/journeys                    → { journeys: [{ id, name, description, category, channels, status, trigger_event? }], count }
//   GET /v1/bot/triggers [?journey=<id>]     → { triggers: [{ category, icon, items: [...] }], count }
//   GET /v1/bot/actions  [?journey=<id>]     → { actions:  [{ category, icon, items: [...] }], count }
// Passing `journey=<id>` scopes triggers/actions down to just that journey's
// own blocks (its one trigger, and only the actions its prebuilt flow uses)
// — omit it for the full, unscoped palette. `id` is the only thing ever sent
// back to the API for a journey; never its name or category.
//
// Call this hook once from each place that needs the catalog
// (create-journey-modal.tsx, builder-page.tsx) — it fetches on first mount
// (and again whenever the journey scope changes) and populates the shared
// JOURNEY_CATEGORIES/ACTION_CATEGORIES/QUICK_ACTIONS exports in catalog.ts,
// which everything else (add-panel, flow-canvas, config-panel) reads
// directly; `journeyTemplates` comes back directly from the hook since only
// the modal renders it. Each scope (unscoped, or one per journey id) is
// cached independently, so switching back to a scope already loaded this
// session skips the network round trip.

// Curated per-category emoji, matching the reference design — keyed by the
// slugified category label rather than the API's own `icon` string, which is
// a plain keyword ("user", "message") with no fixed emoji mapping. Unknown
// categories fall back to a generic icon rather than guessing.
const CATEGORY_EMOJI: Record<string, string> = {
  customer: "👤",
  commerce: "🛒",
  engagement: "💬",
  behavior: "😴",
  support: "🎫",
  time: "📅",
  communication: "📞",
  external: "🔗",
};

// Ids that render as the top 3-up "quick action" grid in the palette rather
// than inside their backend category — stable regardless of which category
// (Logic, Timing, ...) the API groups them under.
const QUICK_ACTION_IDS = new Set(["branch", "condition", "wait"]);

const slugify = (s: string) =>
  s
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "") || "other";

// One loaded-flag per scope ("" = unscoped/full palette, else a journey id)
// — triggers/actions are re-fetched (and the shared catalog.ts globals
// overwritten) whenever the requested scope isn't cached yet.
const loadedScopes = new Set<string>();
let journeyTemplatesCache: { loaded: boolean; templates: JourneyTemplate[] } = { loaded: false, templates: [] };

// The unfiltered actions view per journey scope, kept alongside the
// channel-filtered ones below so toggling the AI Generate channel filter
// on/off (e.g. leaving that node's config panel) can restore it instantly
// without a network round trip.
const baseActionsCache = new Map<string, { categories: ActionCategory[]; quickActions: ActionDef[] }>();
// Keyed `${scopeKey}|${sorted,channels}` — GET /v1/bot/actions?channels=...
// (confirmed live: returns the union of send cards for the given channels,
// excluding every other channel's; building blocks always stay).
const filteredActionsCache = new Map<string, { categories: ActionCategory[]; quickActions: ActionDef[] }>();

function mapTriggerGroups(groups: any[]): JourneyCategory[] {
  return (groups || []).map((g) => {
    const id = slugify(g.category || "other");
    // Per direct ask: every trigger the backend's own catalog lists is
    // treated as usable — same rule as mapActionGroups below, just for
    // triggers. The list itself still comes straight from the backend
    // (GET /v1/bot/triggers); only the "planned" lock on top of it is gone.
    const triggers: TriggerDef[] = (g.items || []).map((item: any) => ({
      id: item.id,
      label: item.label,
      description: item.description,
      status: "available",
    }));
    return { id, label: g.category || "Other", icon: CATEGORY_EMOJI[id] || "🔹", triggers };
  });
}

// Shown under the design's name rather than the backend's — the engine
// still gets the backend type (`nodeType`).
const ACTION_LABEL_OVERRIDES: Record<string, string> = { create_crm_task: "Assign People" };

function mapActionGroups(groups: any[]): { categories: ActionCategory[]; quickActions: ActionDef[] } {
  const quickActions: ActionDef[] = [];
  const categories: ActionCategory[] = [];

  (groups || []).forEach((g) => {
    const id = slugify(g.category || "other");
    const nodes: ActionDef[] = [];

    (g.items || []).forEach((item: any) => {
      const def: ActionDef = {
        id: item.id,
        paletteLabel: ACTION_LABEL_OVERRIDES[item.id] || item.label,
        nodeLabel: ACTION_LABEL_OVERRIDES[item.id] || item.label,
        category: id,
        description: item.description || "",
        // Per direct ask: every action the backend's own catalog lists is
        // treated as usable here, regardless of whatever `status` it comes
        // back with — no action in the palette should ever show as
        // disabled/"Soon" or block the click/drag path. (Triggers keep their
        // real "planned" status below — only this scope changed.)
        status: "available",
        nodeType: item.node_type,
      };
      if (QUICK_ACTION_IDS.has(item.id)) quickActions.push(def);
      else nodes.push(def);
    });

    if (nodes.length) categories.push({ id, label: g.category || "Other", nodes });
  });

  return { categories, quickActions };
}

function mapJourneyTemplates(journeys: any[]): JourneyTemplate[] {
  return (journeys || []).map((j) => ({
    id: j.id,
    name: j.name,
    description: j.description || "",
    category: j.category || "",
    channels: Array.isArray(j.channels) ? j.channels : [],
    defaultChannels: Array.isArray(j.default_channels) ? j.default_channels : undefined,
    status: j.status === "template" ? "template" : "available",
    triggerEvent: typeof j.trigger_event === "string" ? j.trigger_event : undefined,
  }));
}

// `journeyId` scopes the trigger/action catalog to one journey's own blocks
// (see module comment above) — omit it for the full palette. `channels`
// additionally scopes just the actions half to whichever channels are
// currently selected on an AI Generate node's config panel (config-panel.tsx
// passes its own `channels` down through builder-page.tsx) — omit/empty it
// for the unfiltered action palette. Building-block actions (wait/condition/
// branch/fetch profile/ai generate) are unaffected either way; only the
// channel-specific Send cards are added/removed.
export function useJourneyCatalog(journeyId?: string, channels?: string[]) {
  const scopeKey = journeyId || "";
  const channelsKey = channels && channels.length ? Array.from(new Set(channels.map((c) => c.toLowerCase()))).sort().join(",") : "";
  const [loading, setLoading] = useState(!loadedScopes.has(scopeKey));
  const [loadError, setLoadError] = useState(false);
  const [journeyTemplates, setJourneyTemplatesState] = useState<JourneyTemplate[]>(journeyTemplatesCache.templates);
  const { makeRequest: getTriggers } = useJouneyReq("/v1/bot/triggers", "GET");
  const { makeRequest: getActions } = useJouneyReq("/v1/bot/actions", "GET");
  const { makeRequest: getFilteredActions } = useJouneyReq("/v1/bot/actions", "GET");
  const { makeRequest: getJourneys } = useJouneyReq("/v1/bot/journeys", "GET");
  const startedScopeRef = useRef<string | null>(null);

  // Triggers + the unfiltered actions view — re-runs whenever the requested
  // journey scope actually changes (not on every channel toggle).
  useEffect(() => {
    if (loadedScopes.has(scopeKey) || startedScopeRef.current === scopeKey) return;
    startedScopeRef.current = scopeKey;
    setLoading(true);

    (async () => {
      try {
        const params = journeyId ? { journey: journeyId } : undefined;
        const [[triggersRes], [actionsRes]] = await Promise.all([getTriggers(undefined, params), getActions(undefined, params)]);
        setJourneyCategories(mapTriggerGroups(triggersRes?.triggers));
        const base = mapActionGroups(actionsRes?.actions);
        baseActionsCache.set(scopeKey, base);
        if (!channelsKey) setActionCatalog(base.categories, base.quickActions);
        loadedScopes.add(scopeKey);
      } catch {
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey]);

  // Channel-filtered actions — re-runs whenever the AI Generate node's
  // channel selection changes. Restores the cached unfiltered view instantly
  // (no network call) as soon as the selection is cleared/the node is no
  // longer selected, rather than re-fetching the base view again.
  useEffect(() => {
    if (!channelsKey) {
      const base = baseActionsCache.get(scopeKey);
      if (base) setActionCatalog(base.categories, base.quickActions);
      return;
    }
    const cacheKey = `${scopeKey}|${channelsKey}`;
    const cached = filteredActionsCache.get(cacheKey);
    if (cached) {
      setActionCatalog(cached.categories, cached.quickActions);
      return;
    }
    let cancelled = false;
    (async () => {
      const params: Record<string, string> = { channels: channelsKey };
      if (journeyId) params.journey = journeyId;
      const [actionsRes, status] = await getFilteredActions(undefined, params);
      if (cancelled || status < 200 || status >= 300) return;
      const filtered = mapActionGroups(actionsRes?.actions);
      filteredActionsCache.set(cacheKey, filtered);
      setActionCatalog(filtered.categories, filtered.quickActions);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey, channelsKey]);

  // The journey template list itself is scope-independent — fetched once,
  // regardless of which (if any) journey the catalog above is scoped to.
  useEffect(() => {
    if (journeyTemplatesCache.loaded) return;
    (async () => {
      const [journeysRes] = await getJourneys();
      journeyTemplatesCache = { loaded: true, templates: mapJourneyTemplates(journeysRes?.journeys) };
      setJourneyTemplatesState(journeyTemplatesCache.templates);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { loading, loadError, journeyTemplates };
}
